import { PageHeader, Panel, StatTile } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { StatusLabel } from "../components/ListTable";
import { Avatar } from "../components/Avatar";
import { Stars } from "../components/Stars";
import { ThemeToggle } from "../components/ThemeToggle";
import { inputClass, FormRow } from "../components/FormKit";

// Living design-system catalog (Phase 3, Enterprise Design System). Renders the
// semantic tokens + primitives so the system is verifiable at a glance — flip the
// theme toggle to check both light and dark. Every swatch is a token, never a raw color.

const SWATCHES: { name: string; cls: string; text?: boolean }[] = [
  { name: "bg", cls: "bg-bg" },
  { name: "surface", cls: "bg-surface" },
  { name: "surface-2", cls: "bg-surface-2" },
  { name: "line", cls: "bg-line" },
  { name: "ink", cls: "bg-ink" },
  { name: "muted", cls: "bg-muted" },
  { name: "accent", cls: "bg-accent" },
  { name: "accent-strong", cls: "bg-accent-strong" },
  { name: "verified", cls: "bg-verified" },
  { name: "verified-bg", cls: "bg-verified-bg" },
  { name: "info", cls: "bg-info" },
  { name: "warn", cls: "bg-warn" },
  { name: "warn-bg", cls: "bg-warn-bg" },
  { name: "danger", cls: "bg-danger" },
];

const STATUSES = ["accepted", "verified", "pending", "under_review", "held", "needs_info", "processing", "rejected", "clawed_back"];

export function Styleguide() {
  return (
    <div className="mx-auto grid w-full max-w-4xl gap-6">
      <PageHeader
        title="Design system"
        subtitle="Living catalog of tokens and primitives. Flip the theme to verify both modes."
        actions={<ThemeToggle />}
      />

      <Panel title="Color tokens">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
          {SWATCHES.map((s) => (
            <div key={s.name} className="grid gap-1">
              <div className={`h-12 rounded-lg border border-line ${s.cls}`} />
              <span className="font-mono text-[10px] text-muted">{s.name}</span>
            </div>
          ))}
        </div>
        <p className="font-mono text-[11px] text-muted">
          Semantic only — <span className="text-warn">warn</span> (caution) is distinct from the{" "}
          <span className="text-accent-strong">accent</span> brand amber. No raw palette colors exist in the app.
        </p>
      </Panel>

      <Panel title="Typography">
        <div className="grid gap-2">
          <p className="font-display text-3xl font-black tracking-tight">Display · font-black</p>
          <p className="text-base">Body — the quick brown fox jumps over the lazy dog.</p>
          <p className="font-mono text-sm text-muted">Mono · used for status, metrics, and IDs</p>
        </div>
      </Panel>

      <Panel title="Buttons">
        <div className="flex flex-wrap items-center gap-3">
          <Button>Amber (primary)</Button>
          <Button variant="ghost">Ghost</Button>
          <Button disabled>Disabled</Button>
        </div>
      </Panel>

      <Panel title="Status labels">
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {STATUSES.map((s) => <StatusLabel key={s} status={s} />)}
        </div>
      </Panel>

      <Panel title="Stat tiles">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile icon="jobs" label="Postings" value="128" tone="accent" />
          <StatTile icon="verify" label="Verified" value="94" tone="verified" />
          <StatTile icon="flag" label="Under review" value="7" tone="warn" />
          <StatTile icon="bids" label="Disputes" value="2" tone="danger" />
        </div>
      </Panel>

      <Panel title="Forms">
        <div className="grid gap-3 sm:grid-cols-2">
          <FormRow label="Text input"><input className={inputClass} placeholder="Type here…" /></FormRow>
          <FormRow label="Select">
            <select className={inputClass}><option>Option A</option><option>Option B</option></select>
          </FormRow>
        </div>
      </Panel>

      <Panel title="Avatars & ratings">
        <div className="flex flex-wrap items-center gap-4">
          <Avatar name="Lone Star Carriers" className="h-12 w-12 rounded-lg text-lg" />
          <Avatar name="Mike Hauler" className="h-12 w-12 rounded-lg text-lg" tone="bg-verified-bg text-verified" />
          <Stars value={4.6} count={12} />
        </div>
      </Panel>
    </div>
  );
}
